/**
 * Resource Availability — individual worker drill-down.
 * Reuses the monthly view-model the team page builds (a single fetch back to the monthly
 * endpoint so we can reuse the same WorkerView). Daily SP results are also exposed for callers
 * that want per-day granularity, but the default visualization stays at month-level.
 */

import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { usePageTitle } from '@/hooks/use-page-title'
import { useResourceAvailabilityData } from '@/hooks/use-resource-availability-data'
import { defaultMonthWindow } from '@/hooks/use-availability-filters'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { TablePagination } from '@/components/ui/table-pagination'
import { EngagementTimelineWaterfall, type EngagementTimelineRow } from '@/components/resource-availability/EngagementTimelineWaterfall'
import { WorkerBreakdownChart } from '@/components/resource-availability/WorkerBreakdownChart'
import { WorkerMonthlyDetailTable } from '@/components/resource-availability/WorkerMonthlyDetailTable'
import { AvailabilityBanner } from '@/components/resource-availability/AvailabilityBanner'
import { Badge } from '@/components/ui/badge'
import { useSkillsCertificationsCatalog } from '@/hooks/use-skills-certifications-catalog'
import { testId } from '@/lib/utils/test-id'
import { formatHours, formatPercent, weekDayRange } from '@/lib/utils/availability-utils'
import { utcMs } from '@/lib/utils/timeline-axis'
import { requestTokenFromParent } from '@/lib/app-messaging'
import { useLanguage } from '@/contexts/language-context'
import type { Granularity } from '@/types/ui/resource-availability'
import { budgetedHoursLabel } from '@/lib/utils/budgeted-hours-timeline'

// expiryDate is a "yyyy-MM-dd" calendar date with no timezone. `new Date().toISOString()`
// reports the UTC date, which for a negative UTC offset can already be "tomorrow" locally —
// build "today" from local date components and compare the ISO strings directly.
function todayLocalIsoDate(): string {
  const now = new Date()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${now.getFullYear()}-${month}-${day}`
}

export default function ResourceAvailabilityWorkerPage() {
  const { userProfileId: userProfileIdParam } = useParams<{ userProfileId: string }>()
  const userProfileId = Number(userProfileIdParam)
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { dictionary } = useLanguage()
  const t = dictionary?.resourceAvailability?.drilldown
  const tPage = dictionary?.resourceAvailability?.page
  const tToggles = dictionary?.resourceAvailability?.toggles
  const [timelineGranularity, setTimelineGranularity] = useState<Granularity>('monthly')
  // Match the heatmap's 10-row page on the team page. Workers with many overlapping
  // engagements occasionally have 15-25 rows in the timeline; paginating keeps the
  // section's vertical footprint predictable and aligns the visual with the heatmap.
  const [timelinePage, setTimelinePage] = useState(1)
  const TIMELINE_PAGE_SIZE = 10
  const skillsCatalog = useSkillsCertificationsCatalog()

  useEffect(() => {
    const t = setTimeout(() => requestTokenFromParent(), 100)
    return () => clearTimeout(t)
  }, [])

  // Drill-down is a focused detail view — no per-page filter UI. Renders the allocated
  // view consistently regardless of how the user had the team page configured. Team-page
  // filter state is still preserved via the URL params so back-navigation lands them
  // where they left off.
  const { startMonth, endMonth } = useMemo(() => defaultMonthWindow(), [])
  const { data: workers, raw, ptoUnavailable, loading, error } = useResourceAvailabilityData(startMonth, endMonth)

  const worker = workers.find(w => w.userProfileId === userProfileId)

  const pageTitlePrefix = tPage?.title || 'Availability'
  usePageTitle(worker ? `${pageTitlePrefix} · ${worker.workerName}` : pageTitlePrefix)

  const goBack = () => {
    // Forward whatever filter params arrived in the URL so the team page resumes in the
    // same state. If the user landed here directly (no params), we just navigate back to
    // the team-page defaults.
    const incoming = searchParams.toString()
    navigate(incoming ? `/availability?${incoming}` : '/availability')
  }

  if (loading && !worker) {
    return (
      <div className="container mx-auto px-7 py-6 max-w-[1320px]">
        <div className="rounded-md border bg-card p-8 text-center text-sm text-muted-foreground">{t?.loading || 'Loading…'}</div>
      </div>
    )
  }
  if (error || !worker) {
    return (
      <div className="container mx-auto px-7 py-6 max-w-[1320px] space-y-3">
        <Button variant="ghost" size="sm" onClick={goBack}>
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          {t?.backToTeam || 'Back to team'}
        </Button>
        <div className="rounded-md border border-red-300 bg-red-50 p-4 text-sm text-red-800">
          {error ?? (t?.notFound || 'Worker not found in current window.')}
        </div>
      </div>
    )
  }

  const currentCell = worker.months.find(m => m.isCurrentMonth) ?? worker.months[0]
  const currentPct = currentCell ? currentCell.allocatedPercent : 0
  const avgPct = worker.months.length > 0
    ? Math.round(
        worker.months.reduce((s, c) => s + c.allocatedPercent, 0) / worker.months.length
      )
    : 0

  // Day-precision "full availability starts" — day after the worker's latest engagement
  // commitment ends. Pulls from the raw spans map so engagements running past the 12-month
  // window still produce an accurate date instead of getting clamped to the window edge.
  const fullAvailabilityIso = computeFullAvailabilityStart(raw, userProfileId)
  const todayIso = new Date().toISOString().slice(0, 10)
  const isAvailableNow = !fullAvailabilityIso || fullAvailabilityIso <= todayIso
  const availabilityLabel = isAvailableNow
    ? (t?.nowLabel || 'Now')
    : formatFullDate(fullAvailabilityIso!)
  const availabilitySubtext = isAvailableNow
    ? (t?.fullAvailabilityNoEng || 'No active engagements')
    : (t?.fullAvailabilitySubtext || 'After all current engagements end')

  const totalPto = worker.months.reduce((s, c) => s + c.ptoHours, 0)
  const ptoDayCount = Math.round(totalPto / Math.max(1, worker.hoursPerDay))
  const ptoDaysText = (ptoDayCount === 1
    ? (t?.plannedPtoDaysSingular || '{n} day over 12 months')
    : (t?.plannedPtoDaysPlural || '{n} days over 12 months')
  ).replace('{n}', String(ptoDayCount))
  const headerSubtext = worker.activeEngagementCount === 1
    ? (t?.engagementSingular || 'engagement')
    : (t?.engagementPlural || 'engagements')

  // Engagement rows for the waterfall: from the activeEngagementIds in cells, look up the
  // engagement-level dates and the worker's actual first/last day on each engagement so the
  // waterfall can render sub-month overlays. Sorted here (instead of relying on the
  // waterfall's internal sort) so pagination slices a stable, deterministic order.
  const timelineRows: EngagementTimelineRow[] = useMemo(() => {
    const seen = new Set<string>()
    for (const cell of worker.months) {
      for (const id of cell.activeEngagementIds) seen.add(id)
    }
    const bhpdMap = raw?.workerEngagementBudgetedHoursPerDay?.[String(userProfileId)]
    const timelinesMap = raw?.workerEngagementBudgetedHoursTimelines?.[String(userProfileId)]
    const spansMap = raw?.workerEngagementSpans?.[String(userProfileId)]
    const result: EngagementTimelineRow[] = []
    for (const id of seen) {
      const dates = raw?.engagementDates?.[id]
      // Without engagement-level dates we can't position the lighter overlay, so skip — the
      // waterfall is a strict improvement over the old layout but isn't a fallback view.
      if (!dates) continue
      const span = spansMap?.[id]
      result.push({
        engagementId: id,
        engagementName: raw?.engagementNamesById?.[id],
        engagementStart: dates.startDate,
        engagementEnd: dates.endDate,
        workerStart: span?.workerStart ?? null,
        workerEnd: span?.workerEnd ?? null,
        budgetedHoursPerDay: bhpdMap?.[id],
        budgetedHoursLabel: budgetedHoursLabel(
          bhpdMap?.[id],
          timelinesMap?.[id],
          span?.workerStart ?? dates.startDate,
          span?.workerEnd ?? dates.endDate
        ),
      })
    }
    // Same comparator the waterfall uses internally: earliest worker start first, then
    // longest commitment. Replicated here so pagination doesn't carve a different slice
    // than what the waterfall would render in unpaged mode.
    return result.sort((a, b) => {
      const as = utcMs(a.workerStart ?? a.engagementStart)
      const bs = utcMs(b.workerStart ?? b.engagementStart)
      if (as !== bs) return as - bs
      const ae = utcMs(a.workerEnd ?? a.engagementEnd)
      const be = utcMs(b.workerEnd ?? b.engagementEnd)
      return be - ae
    })
  }, [worker, raw, userProfileId])

  const totalTimelinePages = Math.max(1, Math.ceil(timelineRows.length / TIMELINE_PAGE_SIZE))
  // Derive the in-range page synchronously: navigating from a worker on page 3 to one with
  // a single page would otherwise render an empty slice on the first render (with the
  // clamping effect catching up only afterward). Slice + UI both consume `safeTimelinePage`
  // so they always agree with the data; the effect still runs to keep the persisted state
  // in sync once the clamp has actually changed the visible page.
  const safeTimelinePage = Math.min(Math.max(1, timelinePage), totalTimelinePages)
  useEffect(() => {
    if (timelinePage !== safeTimelinePage) setTimelinePage(safeTimelinePage)
  }, [timelinePage, safeTimelinePage])
  const pagedTimelineRows = useMemo(
    () => timelineRows.slice((safeTimelinePage - 1) * TIMELINE_PAGE_SIZE, safeTimelinePage * TIMELINE_PAGE_SIZE),
    [timelineRows, safeTimelinePage]
  )

  return (
    <div className="container mx-auto px-4 md:px-7 py-6 max-w-[1320px] space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-50 text-blue-700 text-base font-semibold">
            {worker.workerInitials}
          </span>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{worker.workerName}</h1>
            <p className="text-xs text-muted-foreground">
              {worker.workerRole && <span>{worker.workerRole} · </span>}
              {worker.activeEngagementCount} {headerSubtext}
            </p>
            {((worker.skills?.length ?? 0) > 0 || (worker.certifications?.length ?? 0) > 0) && (
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {(worker.skills ?? []).map(s => (
                  <Badge
                    key={`skill-${s.skillId}`}
                    variant="secondary"
                    className="font-normal"
                    data-testid={testId('resource-availability-worker-skill', s.skillId)}
                  >
                    {skillsCatalog.getSkillName(s.skillId) ?? `Skill ${s.skillId}`}
                  </Badge>
                ))}
                {(worker.certifications ?? []).map(c => {
                  const expired = !!c.expiryDate && c.expiryDate < todayLocalIsoDate()
                  return (
                    <Badge
                      key={`cert-${c.certificationId}`}
                      variant="secondary"
                      className="font-normal"
                      data-testid={testId('resource-availability-worker-certification', c.certificationId)}
                    >
                      {skillsCatalog.getCertificationName(c.certificationId) ?? `Certification ${c.certificationId}`}
                      {expired && (
                        <Badge
                          variant="destructive"
                          className="ml-1.5 px-1.5 py-0 text-[10px]"
                          data-testid={testId('resource-availability-worker-certification-expired', c.certificationId)}
                        >
                          {t?.expiredCertification || 'Expired'}
                        </Badge>
                      )}
                    </Badge>
                  )
                })}
              </div>
            )}
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={goBack}>
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          {t?.backToTeam || 'Back to team'}
        </Button>
      </div>

      {ptoUnavailable && (
        <AvailabilityBanner message={t?.ptoUnavailable || 'PTO data unavailable — planned PTO is not shown.'} />
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">{t?.currentAllocated || 'Current allocated'}</p>
          <p className="text-2xl font-semibold">{formatPercent(currentPct)}</p>
          <p className="text-xs text-muted-foreground mt-1">{t?.currentSubtext || 'This month (actual)'}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">{t?.avgForecastAlloc || 'Avg forecast alloc'}</p>
          <p className="text-2xl font-semibold">{formatPercent(avgPct)}</p>
          <p className="text-xs text-muted-foreground mt-1">{t?.avgSubtext || 'Next 12 months'}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">{t?.fullAvailabilityStarts || 'Full availability starts'}</p>
          <p className={`text-2xl font-semibold ${isAvailableNow ? 'text-red-600' : 'text-green-600'}`}>{availabilityLabel}</p>
          <p className="text-xs text-muted-foreground mt-1">{availabilitySubtext}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">{t?.plannedPto || 'Planned PTO'}</p>
          <p className="text-2xl font-semibold">{formatHours(totalPto)}</p>
          <p className="text-xs text-muted-foreground mt-1">{ptoDaysText}</p>
        </CardContent></Card>
      </div>

      <section>
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <h2 className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">{t?.engagementTimeline || 'Engagement timeline'}</h2>
          {timelineRows.length > 0 && (
            <ToggleGroup
              type="single"
              value={timelineGranularity}
              onValueChange={(v: string) => v && setTimelineGranularity(v as Granularity)}
              aria-label={tToggles?.granularityAria || 'Timeline granularity'}
              className="bg-muted rounded-md p-0.5 border-0 h-8"
            >
              <ToggleGroupItem
                value="monthly"
                className="text-xs px-2.5 h-7 border-0 text-foreground/70 hover:text-foreground hover:bg-transparent data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow-sm data-[state=on]:font-medium"
              >
                {tToggles?.granularityMonthly || 'Monthly'}
              </ToggleGroupItem>
              <ToggleGroupItem
                value="weekly"
                className="text-xs px-2.5 h-7 border-0 text-foreground/70 hover:text-foreground hover:bg-transparent data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow-sm data-[state=on]:font-medium"
              >
                {tToggles?.granularityWeekly || 'Weekly'}
              </ToggleGroupItem>
            </ToggleGroup>
          )}
        </div>
        {timelineRows.length === 0 ? (
          <Card><CardContent className="p-6 text-center text-sm text-purple-600">{t?.timelineEmpty || 'No active engagements'}</CardContent></Card>
        ) : (
          <>
            <EngagementTimelineWaterfall
              rows={pagedTimelineRows}
              granularity={timelineGranularity}
              months={
                timelineGranularity === 'weekly'
                  ? worker.weeklyCells.map(c => {
                      // Weekly buckets: monthStart is the week-Monday ISO; compute the week-end
                      // (Sunday) so the timeline axis doesn't compute end-of-month off the
                      // Monday date. Day-range label drops the month prefix since the top tier
                      // already groups by month.
                      const start = new Date(c.monthStart + 'T00:00:00Z')
                      const end = new Date(start)
                      end.setUTCDate(end.getUTCDate() + 6)
                      const endIso = end.toISOString().slice(0, 10)
                      return {
                        monthStart: c.monthStart,
                        monthLabel: weekDayRange(c.monthStart),
                        isCurrentMonth: c.isCurrentMonth,
                        endIso,
                      }
                    })
                  : worker.months.map(m => ({
                      monthStart: m.monthStart,
                      monthLabel: m.monthLabel,
                      isCurrentMonth: m.isCurrentMonth,
                    }))
              }
            />
            <TablePagination
              currentPage={safeTimelinePage}
              totalCount={timelineRows.length}
              pageSize={TIMELINE_PAGE_SIZE}
              onPageChange={setTimelinePage}
            />
          </>
        )}
      </section>

      <section>
        <h2 className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-3">{t?.breakdownTitle || 'Allocation breakdown — 12-month forecast'}</h2>
        <WorkerBreakdownChart worker={worker} />
      </section>

      <section>
        <h2 className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-3">{t?.monthlyForecast || 'Monthly forecast'}</h2>
        <WorkerMonthlyDetailTable worker={worker} mode="allocated" />
      </section>
    </div>
  )
}

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function formatFullDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00Z')
  return `${MONTH_ABBR[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`
}

function computeFullAvailabilityStart(
  raw: ReturnType<typeof useResourceAvailabilityData>['raw'],
  userProfileId: number
): string | null {
  const spansMap = raw?.workerEngagementSpans?.[String(userProfileId)]
  if (!spansMap) return null
  let latestEnd: string | null = null
  for (const [engId, span] of Object.entries(spansMap)) {
    const dates = raw?.engagementDates?.[engId]
    const effectiveEnd = span.workerEnd ?? dates?.endDate ?? null
    if (effectiveEnd && (latestEnd === null || effectiveEnd > latestEnd)) {
      latestEnd = effectiveEnd
    }
  }
  if (!latestEnd) return null
  // First day after the latest commitment.
  const d = new Date(latestEnd + 'T00:00:00Z')
  d.setUTCDate(d.getUTCDate() + 1)
  return d.toISOString().slice(0, 10)
}
